import {
  FOC_TOUR_LABEL_DMOB,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import { FOC_DMOB_TOUR_HREF as HREF } from '../focTourHrefs';
import {
  FOC_REQUIRED_DISPOSITION,
  focDmobTourPopoverHtml,
  requirement,
  type FocDmobTourStepDef,
} from './focDmobTourTypes';

export const FOC_DMOB_TOUR_PARAM = 'focDmobTour';
export const FOC_DMOB_TOUR_HREF = HREF;
export { focDmobTourPopoverHtml };

export const FOC_DMOB_TOUR_IAP_PARAM = 'focDmobTour';
export const FOC_DMOB_SEAT_SLUG = 'demobilization-unit-leader';

export function focDmobTourSeatHref(incidentId: string): string {
  return `/iap/${incidentId}/seat/${FOC_DMOB_SEAT_SLUG}?${FOC_DMOB_TOUR_IAP_PARAM}=1`;
}

export const FOC_DMOB_TOUR_STEPS: FocDmobTourStepDef[] = [
  {
    id: 'sectionIntro',
    tour: 'demob-workspace',
    title: FOC_TOUR_LABEL_DMOB,
    description:
      'Demobilization planning runs from early incident stages through closeout—surplus identification, tentative release, check-out, return status, and documentation in one Demobilization Unit workspace.',
  },
  requirement({
    id: 'dmob001',
    requirementId: 'FOC-DMOB-001',
    name: 'Demobilization Planning',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'demob-workspace',
    description:
      'PRATUS supports demobilization planning from early incident stages through closeout, including surplus resources, tentative release, command approval, notification, check-out, release, return status, equipment recovery, facility closure, financial reconciliation, and documentation completion.',
    bullets: [
      'FOC-DMOB-001a — Demobilization Plan Components: general information; responsibilities; release priorities/procedures; check-out; debrief; performance evaluation; lessons learned; transportation/travel; return of equipment/property; check-out stops; directories/maps/instructions; resource return status; ICS 221-CG and ICS 236-CG products.',
    ],
  }),
  requirement({
    id: 'dmob002',
    requirementId: 'FOC-DMOB-002',
    name: 'Resource Release Lifecycle',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'demob-workspace',
    description:
      'Lifecycle: identify excess → Tentative Release List → command approval → notify → report to Demobilization Unit → complete check-out/ICS 221 → release → home unit receives return info → Demobilization Unit tracks return status.',
  }),
  requirement({
    id: 'dmob003',
    requirementId: 'FOC-DMOB-003',
    name: 'Demobilization Views',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'demob-workspace',
    description:
      'Authorized resource-management and demobilization users get table, calendar, Gantt-chart, and T-card-style views.',
  }),
  requirement({
    id: 'dmob004',
    requirementId: 'FOC-DMOB-004',
    name: 'Demobilization Coordination',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'demob-workspace',
    description:
      'Coordination among Planning, Operations, Logistics, Finance/Admin, Resources, Documentation, Intelligence and Investigations when activated, Command Staff, and external organizations as applicable.',
  }),
  requirement({
    id: 'dmob005',
    requirementId: 'FOC-DMOB-005',
    name: 'Incident Personnel Performance Evaluation / ICS 225',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'demob-ics225',
    description:
      'Supports incident personnel performance evaluation as part of demobilization/closeout and produces the current approved ICS 225-CG when an evaluation is required or directed—as an operational workflow, not only a blank form.',
    bullets: [
      'FOC-DMOB-005a — Evaluation Requirement and Status: which personnel/assignments need evaluation; evaluator; due timing; status (not required, pending, assigned, in progress, ready for review/signature, completed, etc.).',
      'FOC-DMOB-005b — Official ICS 225 Data Mapping: all approved USCG ICS 225-CG fields, ratings, narratives, signatures; prepopulate from authoritative records.',
      'FOC-DMOB-005c — Evaluator Workflow: open from incident/personnel/demob context; draft; identify missing content; review with evaluated member; signature/date actions.',
      'FOC-DMOB-005d — Evaluator Reassignment and Exception Handling: reassign with reason and audit; retain prior workflow history.',
      'FOC-DMOB-005e — Demobilization Dependency Visibility: outstanding vs complete ICS 225 before release; minimum status for users without evaluation content access; flag without hard-stopping release unless Coast Guard process requires it.',
      'FOC-DMOB-005f — Privacy, Access, and Record Integrity: role-specific access; version/signature/audit; no unauthorized exposure via search, COP, collaboration, notifications, or AI.',
      'FOC-DMOB-005g — Source-Faithful ICS 225 Export in the current approved USCG layout.',
    ],
  }),
  requirement({
    id: 'dmob006',
    requirementId: 'FOC-DMOB-006',
    name: 'ICS 221 Demobilization Check-Out Workflow',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'demob-ics221',
    description:
      'Implements the current approved ICS 221-CG as a demobilization check-out workflow: required stops, completion tracking, responsible position, remarks/clearances, incomplete actions, and source-faithful export without a disconnected duplicate checklist.',
    bullets: [
      'FOC-DMOB-006a — Check-Out Dependency Integration: uses authoritative resource/personnel, equipment/property, Logistics, Finance/Admin, Documentation, and other closeout data; completed actions update demobilization workspace and release status.',
    ],
  }),
  requirement({
    id: 'dmob007',
    requirementId: 'FOC-DMOB-007',
    name: 'ICS 236 Tentative Release List Workflow',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'demob-ics236',
    description:
      'Supports development, review, approval, revision, and source-faithful export of the current approved ICS 236-CG Tentative Release List, reusing authoritative resource records with planned release timing, status, and responsible organization/position.',
  }),
  requirement({
    id: 'dmob008',
    requirementId: 'FOC-DMOB-008',
    name: 'Demobilization Completion Gate and Closeout Readiness',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'demob-workspace',
    description:
      'Before marking a resource/personnel record or incident fully demobilized/closed, authorized users see incomplete dependencies such as ICS 214, 221, 225 when applicable, return/travel/finance/documentation, external access termination, and home-unit return status.',
    bullets: [
      'FOC-DMOB-008a — No Silent Closeout: outstanding requirements are not silently marked complete; overrides/N/A determinations record user, date/time, and reason when permitted.',
    ],
  }),
  requirement({
    id: 'dmob009',
    requirementId: 'FOC-DMOB-009',
    name: 'Incident Closeout, Debrief, AAR, and Improvement Items',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'demob-workspace',
    description:
      'Supports closeout via debrief status, final products, follow-up actions, AAR observations, improvement items with owners/due dates, archive status, and closure decision. Lessons learned stay distinguishable from approved doctrine.',
    bullets: [
      'FOC-DMOB-009a — Closeout-to-Learning Pipeline: authorized program users can later review approved AAR/exercise/incident observations for requirement, training, job-aid, template, or AI-corpus updates without automatically modifying the FOC baseline.',
    ],
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_DMOB),
  },
];

export function focDmobTourNavVisible(enabled: boolean): boolean {
  return enabled;
}

export function focDmobTourStepIds(): string[] {
  return FOC_DMOB_TOUR_STEPS.map((step) => step.id);
}
